import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';

import {
  applyMarkdownAction,
  insertBlock,
  type MarkdownAction,
} from '../lib/markdown-format.ts';
import { mentionQueryAt } from '../lib/mentions.ts';
import { api, type MentionUser } from '../live/api.ts';
import { useStore } from '../live/context.tsx';
import { Markdown } from './markdown.tsx';

const TOOLS: { action: MarkdownAction; label: string; title: string }[] = [
  { action: 'bold', label: 'B', title: 'Bold' },
  { action: 'italic', label: 'I', title: 'Italic' },
  { action: 'code', label: '</>', title: 'Inline code' },
  { action: 'heading', label: 'H', title: 'Heading' },
  { action: 'ul', label: '-', title: 'Bulleted list' },
  { action: 'ol', label: '1.', title: 'Numbered list' },
  { action: 'quote', label: '>', title: 'Quote' },
  { action: 'fence', label: '{ }', title: 'Code block' },
  { action: 'link', label: 'Link', title: 'Link' },
];

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_MENTION_RESULTS = 8;

interface ActiveMention {
  start: number;
  end: number;
  query: string;
}

interface Props {
  id: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  initialMode?: 'write' | 'preview';
  disabled?: boolean;
  /**
   * Project slug the text belongs to. When set, images can be pasted, dropped or picked: they
   * upload to that project and land in the text as `![name](/api/attachments/…)`. Without it
   * the composer is text-only.
   */
  project?: string | null;
  /** Offer project-visible Nightshift users after `@`. Ticket text fields opt in explicitly. */
  enableMentions?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}

function imageFiles(list: FileList | DataTransferItemList | null | undefined): File[] {
  if (!list) return [];
  const files: File[] = [];
  for (const entry of Array.from(list as ArrayLike<File | DataTransferItem>)) {
    const file = entry instanceof File ? entry : entry.kind === 'file' ? entry.getAsFile() : null;
    if (file && IMAGE_TYPES.has(file.type)) files.push(file);
  }
  return files;
}

function placeholderFor(name: string, token: number): string {
  return `![Uploading ${name}…](uploading-${token})`;
}

/**
 * Markdown write surface: steel mark buttons, Write/Preview, and the same
 * renderer the ticket already uses for posted comments. Given a project, it also
 * takes images: paste, drop, or the Image button. Each one gets a placeholder at the
 * caret the moment it is chosen, which the finished upload replaces in place, so the
 * operator keeps typing while the bytes travel.
 */
export function MarkdownComposer({
  id,
  value,
  onChange,
  placeholder,
  rows = 4,
  initialMode = 'write',
  disabled = false,
  project = null,
  enableMentions = false,
  onUploadingChange,
}: Props) {
  const store = useStore();
  const area = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const pending = useRef<{ start: number; end: number } | null>(null);
  // The latest text, so an upload that finishes after further typing splices into the
  // current value rather than the one it started from.
  const latest = useRef(value);
  latest.current = value;
  const tokens = useRef(0);
  const [mode, setMode] = useState<'write' | 'preview'>(initialMode);
  const [dropping, setDropping] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([]);
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [activeMention, setActiveMention] = useState(0);
  const canAttach = Boolean(project) && !disabled;
  const mentionResults = mention
    ? mentionUsers
        .filter((user) =>
          user.username.startsWith(mention.query) || user.displayName.toLowerCase().includes(mention.query),
        )
        .slice(0, MAX_MENTION_RESULTS)
    : [];

  useEffect(() => {
    onUploadingChange?.(uploading > 0);
  }, [uploading, onUploadingChange]);

  useEffect(() => {
    setMention(null);
    setMentionUsers([]);
    if (!enableMentions || !project) return;
    let cancelled = false;
    void api
      .mentionUsers(project)
      .then((result) => {
        if (!cancelled) setMentionUsers(result.users);
      })
      .catch(() => {
        if (!cancelled) setMentionUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enableMentions, project]);

  useEffect(() => {
    const node = area.current;
    const sel = pending.current;
    if (!node || !sel) return;
    node.focus();
    node.setSelectionRange(sel.start, sel.end);
    pending.current = null;
  }, [value]);

  function apply(action: MarkdownAction) {
    const node = area.current;
    const start = node?.selectionStart ?? value.length;
    const end = node?.selectionEnd ?? value.length;
    const next = applyMarkdownAction({ value, start, end }, action);
    pending.current = { start: next.start, end: next.end };
    onChange(next.value);
    setMode('write');
  }

  function attach(files: File[]) {
    if (!project || files.length === 0) return;
    const node = area.current;
    let text = latest.current;
    // At the caret when the operator is in the text; otherwise (Image button, drop on an
    // unfocused surface) at the end, which is where an unfocused textarea's caret is not.
    let caret = node && document.activeElement === node ? node.selectionEnd : text.length;
    const jobs: Array<{ file: File; marker: string }> = [];
    for (const file of files) {
      if (file.size > MAX_IMAGE_BYTES) {
        store.toast('bad', `${file.name || 'Image'} is over 8 MB`);
        continue;
      }
      tokens.current += 1;
      const marker = placeholderFor(file.name || 'image', tokens.current);
      const inserted = insertBlock({ value: text, start: caret, end: caret }, marker);
      text = inserted.value;
      caret = inserted.end;
      jobs.push({ file, marker });
    }
    if (jobs.length === 0) return;
    pending.current = { start: caret, end: caret };
    latest.current = text;
    onChange(text);
    setMode('write');
    setUploading((count) => count + jobs.length);
    for (const job of jobs) {
      void api
        .uploadAttachment(project, job.file)
        .then((uploaded) => replaceMarker(job.marker, uploaded.markdown))
        .catch((error: unknown) => {
          replaceMarker(job.marker, '');
          store.toast('bad', error instanceof Error ? error.message : String(error));
        })
        .finally(() => setUploading((count) => Math.max(0, count - 1)));
    }
  }

  function replaceMarker(marker: string, replacement: string) {
    const current = latest.current;
    const at = current.indexOf(marker);
    if (at < 0) return;
    // A failed upload leaves nothing behind, including the line the placeholder sat on.
    const cut = replacement.length === 0 && current[at + marker.length] === '\n' ? marker.length + 1 : marker.length;
    const next = current.slice(0, at) + replacement + current.slice(at + cut);
    latest.current = next;
    onChange(next);
  }

  function onPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    if (!canAttach) return;
    const files = imageFiles(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    attach(files);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    setDropping(false);
    if (!canAttach) return;
    const files = imageFiles(event.dataTransfer.files);
    if (files.length === 0) return;
    event.preventDefault();
    attach(files);
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    if (!canAttach || !Array.from(event.dataTransfer.types).includes('Files')) return;
    event.preventDefault();
    if (!dropping) setDropping(true);
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const files = imageFiles(event.target.files);
    event.target.value = '';
    attach(files);
  }

  function updateMention(text: string, caret: number) {
    if (!enableMentions) {
      setMention(null);
      return;
    }
    const next = mentionQueryAt(text, caret);
    if (!next) {
      setMention(null);
      return;
    }
    setMention(next);
    setActiveMention(0);
  }

  function selectMention(user: MentionUser) {
    if (!mention) return;
    const suffix = value[mention.end] && !/\s/.test(value[mention.end]!) ? ' ' : '';
    const inserted = `@${user.username}${suffix}`;
    const next = value.slice(0, mention.start) + inserted + value.slice(mention.end);
    const caret = mention.start + inserted.length;
    pending.current = { start: caret, end: caret };
    setMention(null);
    onChange(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (mention && mentionResults.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setActiveMention((current) =>
          (current + direction + mentionResults.length) % mentionResults.length,
        );
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectMention(mentionResults[activeMention] ?? mentionResults[0]!);
        return;
      }
    }
    if (event.key === 'Escape' && mention) {
      event.preventDefault();
      setMention(null);
      return;
    }
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    const action =
      key === 'b'
        ? 'bold'
        : key === 'i'
          ? 'italic'
          : key === 'e'
            ? 'code'
            : key === 'k'
              ? 'link'
              : null;
    if (!action) return;
    event.preventDefault();
    apply(action);
  }

  return (
    <div
      className={`md-composer${dropping ? ' is-dropping' : ''}`}
      onDragOver={onDragOver}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      <div className="md-composer-bar">
        <div className="md-composer-marks" role="toolbar" aria-label="Markdown formatting">
          {TOOLS.map((tool) => (
            <button
              key={tool.action}
              type="button"
              className={`md-composer-mark${tool.action === 'italic' ? ' is-italic' : ''}${tool.action === 'code' || tool.action === 'fence' ? ' is-mono' : ''}`}
              title={tool.title}
              aria-label={tool.title}
              disabled={disabled}
              onClick={() => apply(tool.action)}
            >
              {tool.label}
            </button>
          ))}
          {project ? (
            <>
              <button
                type="button"
                className="md-composer-mark"
                title="Attach an image (or paste one)"
                aria-label="Attach an image"
                disabled={!canAttach}
                onClick={() => picker.current?.click()}
              >
                Image
              </button>
              <input
                ref={picker}
                className="md-composer-file"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                multiple
                tabIndex={-1}
                aria-hidden="true"
                onChange={onPick}
              />
            </>
          ) : null}
        </div>
        <div className="md-composer-modes" role="tablist" aria-label="Comment view">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'write'}
            className={`md-composer-mode${mode === 'write' ? ' is-current' : ''}`}
            disabled={disabled}
            onClick={() => setMode('write')}
          >
            Write
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'preview'}
            className={`md-composer-mode${mode === 'preview' ? ' is-current' : ''}`}
            disabled={disabled}
            onClick={() => setMode('preview')}
          >
            Preview
          </button>
        </div>
      </div>
      {mode === 'write' ? (
        <div className="md-composer-write">
          <textarea
            ref={area}
            id={id}
            className="input md-composer-input"
            rows={rows}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            role={enableMentions ? 'combobox' : undefined}
            aria-autocomplete={enableMentions ? 'list' : undefined}
            aria-expanded={enableMentions ? mentionResults.length > 0 : undefined}
            aria-controls={enableMentions ? `${id}-mentions` : undefined}
            aria-activedescendant={
              enableMentions && mentionResults[activeMention]
                ? `${id}-mention-${mentionResults[activeMention]!.id}`
                : undefined
            }
            onChange={(event) => {
              onChange(event.target.value);
              updateMention(event.target.value, event.target.selectionStart);
            }}
            onClick={(event) => updateMention(event.currentTarget.value, event.currentTarget.selectionStart)}
            onKeyUp={(event) => {
              if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(event.key)) {
                updateMention(event.currentTarget.value, event.currentTarget.selectionStart);
              }
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
          />
          {mentionResults.length > 0 ? (
            <div className="md-mention-menu" id={`${id}-mentions`} role="listbox" aria-label="Mention a user">
              {mentionResults.map((user, index) => (
                <button
                  key={user.id}
                  id={`${id}-mention-${user.id}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeMention}
                  className={`md-mention-option${index === activeMention ? ' is-current' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectMention(user)}
                >
                  <span className="mono">@{user.username}</span>
                  <span className="dim">{user.displayName}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="md-composer-preview" aria-live="polite">
          {value.trim().length === 0 ? (
            <p className="dim">Nothing to preview yet.</p>
          ) : (
            <Markdown source={value} />
          )}
        </div>
      )}
      {project && mode === 'write' ? (
        <div className={`md-composer-hint${uploading > 0 ? ' is-busy' : ''}`} aria-live="polite">
          {uploading > 0
            ? `Uploading ${uploading === 1 ? 'image' : `${uploading} images`}…`
            : 'Paste or drop an image to attach it.'}
        </div>
      ) : null}
    </div>
  );
}
